import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import VerbamailIcon from "../../../../../../../assets/svg/verbamail-icon";
import PlusFilledIcon from "../../../../../../../assets/svg/plus-filled-icon";
import OutlookImage from "../../../../../../../assets/images/Outlook.png";
import CrossIcon from "../../../../../../../assets/svg/cross-icon";
import CircularLoader from "../../../../../../../components/Loader/CircularLoader";
import Button from "../../../../../../../components/Button/Button";
import { RootState } from "../../../../../../../redux/store";
import { setOutlookIntegration } from "../../../../../../../redux/slices/authSlice";
import { useConnectOutlookMutation } from "../../../../../../../redux/services/authApi";
import { customToast } from "../../../../../../../utils/custom-toast/custom-toast";
import { getFileTypeIcon } from "../../../../../../../utils/functions/file-icon-extractor";
import type { OutlookConnectStatus } from "../../../../../../../components/ProfileCard/ProfileCard";

interface InboxInputProps {
  onChooseTemplate?: () => void;
  onComposeEmail?: () => void;
}

const InboxInput: React.FC<InboxInputProps> = ({
  onChooseTemplate,
  onComposeEmail,
}) => {
  const dispatch = useDispatch();
  const [outlookConnectStatus, setOutlookConnectStatus] =
    useState<OutlookConnectStatus>("idle");
  const [connectOutlookApi] = useConnectOutlookMutation();

  const authUser = useSelector((state: RootState) => state.auth.user);
  const outlookIntegration = useSelector(
    (state: RootState) => state.auth.outlookIntegration
  );
  const userEmail = authUser?.email || "";

  const handleConnectOutlook = async () => {
    if (
      outlookIntegration?.integration_status ||
      outlookConnectStatus === "connecting" ||
      !userEmail
    )
      return;

    setOutlookConnectStatus("connecting");
    try {
      await connectOutlookApi({ user_email: userEmail }).unwrap();
      dispatch(
        setOutlookIntegration({
          is_integration_available: true,
          integration_status: true,
        })
      );
      setOutlookConnectStatus("idle");
      customToast("Outlook connected successfully", "success");
    } catch {
      setOutlookConnectStatus("failed");
      customToast("Failed to connect Outlook", "error");
    }
  };

  const showBanner =
    outlookIntegration?.is_integration_available === true &&
    outlookIntegration?.integration_status === false;

  const showInput = outlookIntegration?.integration_status === true;

  // const showBanner = true;

  return (
    <div className="shrink-0 px-[12px] py-[12px]">
      <div className="flex flex-col border border-border-gray dark:border-border-gray-dk rounded-lg overflow-hidden shadow-card-subtle">
        {/* Outlook Integration Banner */}
        {showBanner && (
          <div
            className="relative flex w-full items-center justify-between px-[10px] py-[8px]"
            role="region"
            aria-label="Outlook integration"
          >
            <div
              className="pointer-events-none absolute inset-0 bg-gradient-ai-card"
              aria-hidden
            />
            <div className="relative flex items-center gap-[8px]">
              <img
                src={OutlookImage}
                alt=""
                className="h-[24px] w-[24px] shrink-0 object-contain"
                width={24}
                height={24}
              />
              <div className="flex flex-col">
                <span className="text-md font-medium leading-normal text-heading-text dark:text-heading-text-dk">
                  Outlook Integration
                </span>
                <span className="text-xxs font-normal leading-normal text-data dark:text-data-dk">
                  Send emails using your Outlook mailbox
                </span>
              </div>
            </div>
            <div className="relative flex shrink-0 items-center">
              {outlookConnectStatus === "connecting" ? (
                <span className="flex items-center gap-2">
                  <CircularLoader
                    size={16}
                    thickness={2}
                    className="text-gray-placeholder dark:text-gray-placeholder-dk"
                  />
                  <span className="whitespace-nowrap text-sm font-medium text-gray-placeholder dark:text-gray-placeholder-dk">
                    Connecting…
                  </span>
                </span>
              ) : outlookConnectStatus === "failed" ? (
                <span className="flex flex-wrap items-center justify-end gap-2.5">
                  <span className="flex items-center gap-1 rounded-md border border-border-gray bg-pure-white px-2 py-1 shadow-xs dark:border-border-gray-dk dark:bg-pure-white-dk">
                    <CrossIcon className="h-3 w-3 shrink-0 text-red-500" />
                    <span className="text-xs font-medium text-verba-indigo dark:text-verba-indigo-dk">
                      Failed
                    </span>
                  </span>
                  <button
                    type="button"
                    className="cursor-pointer whitespace-nowrap text-sm font-semibold text-verba-blue hover:underline dark:text-verba-blue-dk"
                    onClick={handleConnectOutlook}
                  >
                    Retry &gt;
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  className="cursor-pointer whitespace-nowrap text-sm font-semibold text-verba-blue transition-opacity hover:opacity-90 dark:text-verba-blue-dk"
                  onClick={handleConnectOutlook}
                >
                  Connect &gt;
                </button>
              )}
            </div>
          </div>
        )}

        {/* Input Row */}
        {showInput && (
          <div
            className={`flex items-center justify-between bg-popper-bg dark:bg-popper-bg-dk p-[4px] rounded-lg`}
          >
            <div className="flex items-center gap-[6px] p-[4px] rounded-lg">
              <VerbamailIcon className="w-[18px] h-[18px] text-verba-mail dark:text-verba-mail-dk" />
              <span className="text-md font-medium text-heading-text dark:text-heading-text-dk whitespace-nowrap">
                VerbaMail
              </span>
            </div>
            <div className="flex items-center gap-[8px]">
              <Button
                innerText={
                  <span className="flex items-center gap-[4px]">
                    <img
                      src={getFileTypeIcon("")}
                      alt=""
                      className="w-[18px] h-[18px]"
                    />
                    <span>Choose a Template</span>
                  </span>
                }
                buttonContainerClass="w-fit px-[8px] py-[4px] rounded-lg"
                innerTextClass="text-heading-text dark:text-heading-text-dk text-md font-medium"
                onClick={onChooseTemplate}
              />
              <div className="w-px h-[18px] bg-border-gray dark:bg-border-gray-dk" />
              <Button
                innerText="Reply"
                buttonContainerClass="w-fit px-[8px] py-[4px] rounded-lg"
                innerTextClass="text-heading-text dark:text-heading-text-dk text-md font-medium"
                Icon={PlusFilledIcon}
                iconLeft={true}
                iconClass="w-[18px] h-[18px] text-heading-text dark:text-heading-text-dk"
                onClick={onComposeEmail}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InboxInput;
